import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import {
  checkEmpty,
  checkIf,
  colors,
  debounce,
  generateRandom,
  googleMapsApiKey,
  POLYGON_ID_KEY,
  SNAP_DISTANCE,
} from "../utils";
import { LeftArrowIcon, LocationIcon } from "../assets/icons";
import { Box, Button, Drawer, IconButton, Slide, Typography } from "@mui/material";
import MapsSearch from "../components/Maps/search";
import { LoadingButton } from "@mui/lab";
import Polygon, {
  createContainer,
  createLabel,
  createButtonContainer,
  createRevertButton,
  createInput,
} from "../components/Maps/polygon";
import CrudPanel from "../components/Maps/crudPanel";
import useAreas from "../hooks/useArea";
import { APIProvider } from "@vis.gl/react-google-maps";
import { AdminAreaResponse, CoordinateResponse } from "../openapi/client";
import { setNotification } from "../store/notificationSlice";
import { useDispatch } from "react-redux";
import ConfirmDialog from "../components/Dialog";
import useConfirm from "../hooks/useConfirm";
import { DeleteLabelIcon } from "../components/IconLabel";
interface IProps {
  zones: IZone[];
  overlaysToRemove?: IOverlay[];
  setZones: (zones: IZone[]) => void;
  saveZone: (zone: IZone) => void;
  setOverlays: (overlays: IOverlay[]) => void;
  deleteZone: (zone: IZone) => void;
  createArea: (area: AdminAreaResponse) => Promise<void>;
  updateArea: (area: AdminAreaResponse) => Promise<void>;
  labelsVisible: boolean;
  setLabelsVisible: (visible: boolean) => void;
}

export interface IZone {
  id?: string;
  name?: string;
  strokeColor?: string;
  fillColor?: string;
  coordinates?: google.maps.LatLng[];
  updated?: boolean;
  defaultGeometry?: google.maps.LatLng[];
}

export interface IOverlay extends google.maps.Polygon {
  id: string;
  name: string;
  strokeColor: string;
  fillColor: string;
  coordinates: google.maps.LatLng[];
  defaultGeometry: google.maps.LatLng[];
}

export const getCenter = (overlay: google.maps.Polygon) => {
  const center = overlay
    .getPath()
    .getArray()
    .reduce(
      (acc: { lat: number; lng: number }, point: google.maps.LatLng) => ({
        lat: acc.lat + point.lat() / overlay.getPath().getLength(),
        lng: acc.lng + point.lng() / overlay.getPath().getLength(),
      }),
      { lat: 0, lng: 0 }
    );
  return center;
};

// export const mapPosition = { lat: 25.354826, lng: 51.183884 }; // Qatar
export const mapPosition = { lat: 25.28545, lng: 51.53104 }; // Qatar
export const defaultZonePosition = [
  { lat: 25.29538333269049, lng: 51.53104000000001 },
  { lat: 25.285294785220707, lng: 51.51627712158204 },
  { lat: 25.27846513830239, lng: 51.54614620117189 },
];

export function GoogleMap(props: IProps) {
  const mapRef = useRef<HTMLElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [loader, setLoader] = useState(false);
  const [blur, setBlur] = useState<number>();
  const [forceBlur, setForceBlur] = useState<number>();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const colorIndex = useRef(0);

  let [map, setMap] = useState<google.maps.Map>();
  let [drawingManager, setDrawingManager] = useState<google.maps.drawing.DrawingManager>();
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  // let marker: google.maps.marker.AdvancedMarkerElement;
  let infoWindow: google.maps.InfoWindow;
  let geocoder: google.maps.Geocoder;

  const drawingManagerListener = useRef<google.maps.MapsEventListener>();
  const [dragging, setDragging] = useState(false);

  const [polygons, setPolygons] = useState<
    { id: string; strokeColor: string; fillColor: string; coordinates: google.maps.LatLng[] }[]
  >([]);
  const [overlays, setOverlays] = useState<IOverlay[]>([]);
  const overlaysStateful = useRef<IOverlay[]>([]);
  const zonesStateful = useRef<IZone[]>(props.zones);
  const labelsVisibleRef = useRef<boolean>(props.labelsVisible);

  // Update the ref whenever labelsVisible changes
  useEffect(() => {
    labelsVisibleRef.current = props.labelsVisible;
  }, [props.labelsVisible]);

  // console.log("zonesStatefu inside google map", zonesStateful.current);
  // console.log("mapRef", mapRef.current);
  // console.log("markerRef", markerRef.current);
  // console.log("overlaysStateful", overlaysStateful.current);
  // console.log("props.zones inside google map", props.zones);

  const [infoPopup, setInfoPopup] = useState<{
    isOpen: boolean;
    zone?: IZone | IOverlay | null;
    type: "zone" | "overlay";
    mode: "edit" | "create";
    marker?: google.maps.marker.AdvancedMarkerElement;
  }>({
    isOpen: false,
    zone: null,
    type: "zone",
    mode: "edit",
    marker: undefined,
  });

  const resetInfoPopup = (zone?: IZone | IOverlay, marker?: google.maps.marker.AdvancedMarkerElement) => {
    setInfoPopup({ isOpen: false, zone: null, type: "zone", mode: "edit", marker: undefined });
    if (zone) {
      (zone as IOverlay).setMap(null);
      // remove marker as well
      if (marker) {
        marker.position = null;
      }
      // remove from overlaysStateful using the correct ID comparison
      const polygonId = (zone as IOverlay).get(POLYGON_ID_KEY);
      overlaysStateful.current = overlaysStateful.current.filter((o) => o.get(POLYGON_ID_KEY) !== polygonId);

      // remove from markerRef array as well
      markerRef.current = markerRef.current.filter((m) => m.getAttribute("id") !== polygonId);

      props.setOverlays([...overlaysStateful.current]);
    }
  };

  const getNextColor = useCallback(
    (type: "zone" | "overlay", index: number) => {
      const zoneLength = props.zones.length;
      const polygonLength = polygons.length;
      const total = zoneLength + polygonLength;

      if (type === "zone") {
        if (zoneLength > colors.length) {
          return colors[Number(index) % colors.length];
        }
        return colors[Number(index)];
      } else {
        if (total > colors.length) {
          return colors[Number(index + zoneLength) % colors.length];
        }
        return colors[Number(index + zoneLength)];
      }
    },
    [props.zones, polygons]
  );

  const editZone = useCallback(
    (
      id: string,
      type: "zone" | "overlay",
      mode: "edit" | "create",
      marker?: google.maps.marker.AdvancedMarkerElement
    ) => {
      // const zone = props.zones.find((z) => z.id === id);
      const zone = zonesStateful.current.find((z) => z.id === id);
      const overlay = overlaysStateful.current.find((o) => o.get(POLYGON_ID_KEY) === id);

      setInfoPopup({
        isOpen: true,
        zone: zone || overlay,
        type: type,
        mode,
        marker,
      });
    },
    []
  );

  const clearFocus = useCallback(
    (forceAll = false) => {
      // No-op since all polygons should remain focused/editable
      // overlaysStateful.current.forEach((overlay) => {
      //   overlay.setEditable(false);
      //   overlay.setDraggable(false);
      //   if (forceAll) {
      //     overlay.set("isSelected", false);
      //   }
      // });
      // if (forceAll) {
      //   setForceBlur(generateRandom());
      // } else {
      //   setBlur(generateRandom());
      // }
    },
    [overlays]
  );

  const listener = useCallback(
    (event: any) => {
      drawingManager?.setDrawingMode(null);
      clearFocus();

      const color = getNextColor("overlay", overlaysStateful.current.length);
      const id = crypto.randomUUID();

      setPolygons((prev) => [
        ...prev,
        {
          id,
          strokeColor: color,
          fillColor: color,
          coordinates: event.overlay.getPaths().getArray(),
          defaultGeometry: event.overlay.getPaths().getArray(),
        },
      ]);

      let dragging = false;

      const getDragging = () => dragging;

      event.overlay.set(POLYGON_ID_KEY, id);
      event.overlay.set("strokeColor", color);
      event.overlay.set("fillColor", color);
      event.overlay.set("isSelected", true); // Always selected to show edges

      const center = getCenter(event.overlay);

      const container = createContainer(id);
      // const input = createInput(id);
      const label = createLabel(undefined, true); // Always show labels for new overlays
      label.classList.add("new-overlay"); // Mark as new overlay to ensure visibility
      const buttonContainer = createButtonContainer();
      const revertButton = createRevertButton();

      revertButton.addEventListener("click", () => {
        const polygonId = event.overlay.get(POLYGON_ID_KEY);
        const defaultGeometry = overlaysStateful.current.find((z) => z.id === polygonId)?.defaultGeometry;
        event.overlay.setPaths(defaultGeometry);
        marker.position = getCenter(event.overlay);
        container.querySelector(".gmaps-revert-button")?.classList.remove("gmaps-revert-button-active");
        container.querySelector(".gmaps-save-button")?.classList.remove("gmaps-save-button-active");
      });

      label.addEventListener("click", (ev: any) => {
        editZone(event.overlay.get(POLYGON_ID_KEY), "overlay", "create", marker);
      });

      // input.addEventListener("click", (ev: any) => {
      //   console.log("input for", event.overlay.get(POLYGON_ID_KEY));
      //   // ev.stopPropagation();
      //   // ev.preventDefault();
      //   // input.focus();
      // });

      // input.addEventListener("change", (ev: any) => {
      //   console.log("input", ev.target.value);
      //   event.overlay.set("name", ev.target.value);
      // });

      container.appendChild(label);
      container.appendChild(buttonContainer);
      // Please note that there is no need to have a revert button as the polygon is editable
      // buttonContainer.appendChild(revertButton);

      // const input = document.createElement("input");
      // input.type = "text";
      // input.value = "No name";
      // input.className = "gmaps-label-input";

      let marker = new google.maps.marker.AdvancedMarkerElement({
        map,
        position: center,
        content: container,
        gmpClickable: true,
      });

      marker.setAttribute("id", id);
      markerRef.current.push(marker);
      // marker.addListener("click", (ev: any) => {
      //   console.log("marker clicked");
      //   const container = marker?.content as HTMLElement;
      //   const input = container?.querySelector(".gmaps-input");
      //   if (input) {
      //     console.log("input new", input);
      //     (input as HTMLInputElement).focus();
      //   }
      // });

      event.overlay.addListener("click", (ev: any) => {
        // const container = marker?.content as HTMLElement;
        const container = document.getElementById(event.overlay.get(POLYGON_ID_KEY));
        // const input = container?.querySelector(".gmaps-input");
        const input = document.getElementById(`input-${event.overlay.get(POLYGON_ID_KEY)}`);

        // No selection logic needed since all overlays are always focused
        // event.overlay.set("isSelected", true);
        // overlaysStateful.current.forEach(...);
        // clearFocus(false);
        // event.overlay.setEditable(true);
        // event.overlay.setDraggable(true);
        // if (input) {
        //   (input as HTMLInputElement).focus();
        // }
      });

      event.overlay.addListener("rightclick", (ev: any) => {
        if (ev.vertex != null) {
          event.overlay.getPath().removeAt(ev.vertex);
        } else {
          editZone(event.overlay.get(POLYGON_ID_KEY), "overlay", "create", marker);
        }
      });

      // Remove drag listeners since overlays are now non-draggable
      // event.overlay.addListener("dragstart", ...);
      // event.overlay.addListener("dragend", ...);
      // event.overlay.addListener("drag", ...);

      // Add hover effects for labels when they are hidden
      event.overlay.addListener("mouseover", () => {
        // console.log("Overlay mouseover triggered, labelsVisible:", labelsVisibleRef.current);
        // Use the ref to get the current state
        if (!labelsVisibleRef.current) {
          const container = document.getElementById(event.overlay.get(POLYGON_ID_KEY));
          const label = container?.querySelector(".gmaps-label-tag.hidden");
          if (label) {
            label.classList.add("polygon-hover");
          }
        }
      });

      event.overlay.addListener("mouseout", () => {
        // console.log("Overlay mouseout triggered");
        // Always remove the hover class regardless of current state
        const container = document.getElementById(event.overlay.get(POLYGON_ID_KEY));
        const label = container?.querySelector(".gmaps-label-tag");
        if (label) {
          label.classList.remove("polygon-hover");
        }
      });

      // Add listener for name property changes
      event.overlay.addListener("name_changed", () => {
        // console.log("Name changed to:", event.overlay.get("name"));
        // You can add your custom logic here when the name changes
        // For example, update the label text:
        const container = document.getElementById(event.overlay.get(POLYGON_ID_KEY));
        const label = container?.querySelector(".gmaps-label-tag");
        if (label) {
          label.textContent = event.overlay.get("name") || "No name";
        }
        props.setOverlays([...overlaysStateful.current]);
      });

      const path = event.overlay.getPath();
      path.unbindAll();

      path.addListener("set_at", (index: any) => {
        if (!getDragging()) {
          var snapPoint;
          const activePoint = path.getAt(index);

          if (!event.overlay.get("isSnapped")) {
            const snappableZones = zonesStateful.current
              .filter((p) => p.id !== event.overlay.get(POLYGON_ID_KEY))
              .flatMap((p) => p.coordinates);

            const snappableOverlays = overlaysStateful.current
              .filter((p) => p.get(POLYGON_ID_KEY) !== event.overlay.get(POLYGON_ID_KEY))
              .flatMap((p) => p.getPath().getArray());

            const snappables = [...snappableZones, ...snappableOverlays]
              .filter((point) => !activePoint.equals(point))
              .map((point) => ({
                distance: Math.abs(
                  google.maps.geometry.spherical.computeDistanceBetween(point as google.maps.LatLng, activePoint)
                ),
                point: point,
              }))
              .filter((d) => d.distance < SNAP_DISTANCE)
              .sort((x, y) => x.distance - y.distance);

            if (snappables.length > 0) {
              snapPoint = snappables[0].point;
            }
          }

          if (!!snapPoint && !activePoint.equals(snapPoint)) {
            path.setAt(index, snapPoint);
            event.overlay.set("isSnapped", true);

            // Bring overlay to front when snapping
            const highZIndex = 1000 + (Date.now() % 1000); // Generate a high unique zIndex
            event.overlay.setOptions({ zIndex: highZIndex });
          } else {
            event.overlay.set("isSnapped", false);
          }

          marker.position = getCenter(event.overlay);
        }
      });

      setOverlays((prev) => [...prev, event.overlay]);
      overlaysStateful.current.push(event.overlay);
      props.setOverlays([...overlaysStateful.current]);
      editZone(event.overlay.get(POLYGON_ID_KEY), "overlay", "create", marker);
    },
    [props.zones]
  );

  // Effect to update label visibility when state changes
  useEffect(() => {
    document.querySelectorAll(".gmaps-label-tag").forEach((label) => {
      const container = label.closest(".gmaps-container");
      // Check if this is a newly created overlay (not yet saved)
      const isNewOverlay =
        container && overlaysStateful.current.some((overlay) => overlay.get(POLYGON_ID_KEY) === container.id);

      // Don't hide labels for new overlays that haven't been saved yet
      if (!isNewOverlay) {
        if (!labelsVisibleRef.current) {
          label.classList.add("hidden");
        } else {
          label.classList.remove("hidden");
        }
      }
    });
  }, [labelsVisibleRef.current]);

  useEffect(() => {
    if (props.overlaysToRemove) {
      props.overlaysToRemove.forEach((overlay) => {
        const targetOverlay = overlaysStateful.current.find(
          (o) => o.get(POLYGON_ID_KEY) === overlay.get(POLYGON_ID_KEY)
        );
        if (targetOverlay) {
          targetOverlay.setMap(null);
        }
        // find the marker and remove it as well
        const targetMarker = markerRef.current.find((m) => m.getAttribute("id") === overlay.get(POLYGON_ID_KEY));
        if (targetMarker) {
          targetMarker.position = null;
        }
        markerRef.current = markerRef.current.filter((m) => m.getAttribute("id") !== overlay.get(POLYGON_ID_KEY));
        overlaysStateful.current = overlaysStateful.current.filter(
          (o) => o.get(POLYGON_ID_KEY) !== overlay.get(POLYGON_ID_KEY)
        );
      });
      props.setOverlays([...overlaysStateful.current]);
    }
  }, [props.overlaysToRemove]);

  async function initMap(): Promise<void> {
    // Initialize the map.
    map = new google.maps.Map(document.getElementById("map") as HTMLElement, {
      center: mapPosition,
      zoom: 13,
      mapId: "4504f8b37365c3d0",
      mapTypeControl: false,
      streetViewControl: false,
      cameraControl: false,
      // fullscreenControl: false,
    });

    map.addListener("click", (ev: any) => {
      clearFocus(true);
    });

    // TODO: Causing error
    // map.addListener("dragstart", (ev: any) => {
    //   clearFocus();
    // });

    drawingManager = new google.maps.drawing.DrawingManager({
      drawingControl: true,
      drawingControlOptions: {
        position: google.maps.ControlPosition.TOP_CENTER,
        drawingModes: [google.maps.drawing.OverlayType.POLYGON],
      },
      polygonOptions: {
        strokeOpacity: 0.8,
        strokeWeight: 3,
        fillOpacity: 0.35,
        clickable: true,
        editable: true,
        draggable: false, // Make overlays non-draggable
        zIndex: 1,
      },
    });

    drawingManagerListener.current = google.maps.event.addListener(drawingManager, "overlaycomplete", listener);

    drawingManager.setMap(map);

    //@ts-ignore
    const placeAutocomplete = new google.maps.places.PlaceAutocompleteElement();
    //@ts-ignore
    placeAutocomplete.id = "place-autocomplete-input";
    placeAutocomplete.locationBias = mapPosition;

    // const card = document.getElementById("place-autocomplete-card") as HTMLElement;
    // //@ts-ignore
    // card.appendChild(placeAutocomplete);
    // map.controls[google.maps.ControlPosition.TOP_LEFT].push(card);

    infoWindow = new google.maps.InfoWindow({});
    geocoder = new google.maps.Geocoder();

    // marker.addListener("dragend", (event: any) => {
    //   const position = marker.position as google.maps.LatLng;
    //   geocodeLatLng(geocoder, map as google.maps.Map, infoWindow, position, marker, false);
    // });

    setMap(map);
    setDrawingManager(drawingManager);
  }

  // useEffect(() => {
  //   if (props.zones?.length) {
  //     initMap();
  //   }
  // }, [props.zones.length]);

  useEffect(() => {
    setTimeout(() => {
      initMap();
    }, 1000);
  }, []);

  useEffect(() => {
    zonesStateful.current = [...props.zones];
  }, [props.zones]);

  useEffect(() => {
    if (drawingManagerListener.current) {
      google.maps.event.removeListener(drawingManagerListener.current);
    }
    if (drawingManager) {
      drawingManagerListener.current = google.maps.event.addListener(drawingManager, "overlaycomplete", listener);
    }
  }, [props.zones]);

  return (
    <Fragment>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Box sx={{ width: "100%" }}>
          {/* <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              position: "absolute",
              top: "75px",
              left: "16px",
              right: "16px",
              zIndex: 2,
              // boxShadow: "0px 6px 12px 0px rgba(0, 0, 0, 0.16)",
              borderRadius: "12px",
            }}
          >
            <MapsSearch
              onPlaceSelected={(place) => {
                handlePlaceSelected(place);
              }}
              floatingLayer={true}
            />
          </Box> */}
          <Box sx={{ width: "100%", height: "calc(100vh - 130px)" }} ref={mapRef} id="map">
            {props.zones?.map((polygon, index) => (
              <Polygon
                key={polygon.id}
                id={String(polygon.id)}
                name={polygon.name}
                updated={polygon.updated}
                color={getNextColor("zone", index)}
                coordinates={polygon.coordinates as google.maps.LatLng[]}
                defaultGeometry={polygon.defaultGeometry as google.maps.LatLng[]}
                map={map as google.maps.Map}
                blur={blur}
                clearFocus={clearFocus}
                polygons={overlays}
                zones={props.zones}
                editZone={editZone}
                labelsVisible={props.labelsVisible}
                labelsVisibleRef={labelsVisibleRef}
                saveZone={(zone) => {
                  zonesStateful.current = zonesStateful.current.map((z) => (z.id === zone.id ? { ...zone } : z));
                  props.setZones(zonesStateful.current);
                  props.saveZone(zone);
                }}
                setZone={(zone) => {
                  zonesStateful.current = zonesStateful.current.map((z) => (z.id === zone.id ? { ...zone } : z));
                  props.setZones(zonesStateful.current);
                }}
                deleteZone={(id) => {
                  zonesStateful.current = zonesStateful.current.filter((z) => z.id !== id);
                  props.setZones(zonesStateful.current);
                }}
                forceBlur={forceBlur}
              />
            ))}
          </Box>
        </Box>
      </Box>
      <Drawer
        variant="temporary"
        open={infoPopup.isOpen}
        anchor="right"
        onClose={(e, reason) => {
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            resetInfoPopup();
          }
        }}
      >
        <Box sx={{ width: "450px" }}>
          <CrudPanel
            zone={infoPopup.zone as Partial<IZone & IOverlay>}
            onClose={(zone) => {
              resetInfoPopup(zone, infoPopup.marker);
            }}
            mode={infoPopup.mode}
            createArea={props.createArea}
            updateArea={props.updateArea}
            onConfirm={(zone) => {
              if (infoPopup.type === "zone") {
                zonesStateful.current = zonesStateful.current.map((z) =>
                  z.id === zone.id ? { ...zone, updated: false } : z
                );
                props.setZones(zonesStateful.current);
              } else {
                // const overlay = overlaysStateful.current.find((o) => o.id === zone.id);
                // if (overlay) {
                //   overlay.setMap(null);
                //   (infoPopup.marker as google.maps.marker.AdvancedMarkerElement).position = null;
                // }
                // overlaysStateful.current = overlaysStateful.current.filter((o) => o.id !== zone.id);

                const overlay = overlaysStateful.current.find((o) => {
                  return o.get(POLYGON_ID_KEY) === (zone as any)[POLYGON_ID_KEY];
                });
                overlay?.set("name", zone.name);
                props.setOverlays([...overlaysStateful.current]);
                // If you need to manually trigger the notification, you can use:
                // overlay?.notify("name");
              }
              resetInfoPopup();
            }}
            onDelete={props.deleteZone}
          />
        </Box>
      </Drawer>
    </Fragment>
  );
}

export default function Areas() {
  const [ConfirmDialog, confirmDelete] = useConfirm({
    titleIcon: <DeleteLabelIcon />,
    text: "You are about to permanently delete an area. Are you sure you want to proceed?",
  });

  useEffect(() => {
    initMap();
  }, []);

  const dispatch = useDispatch();
  const [updateFlag, setUpdateFlag] = useState(false);
  const [loading, setLoading] = useState(false);
  const [labelsVisible, setLabelsVisible] = useState(true);
  const { areaList: zonesReference, updateArea, deleteArea } = useAreas();
  const [overlaysToRemove, setOverlaysToRemove] = useState<IOverlay[]>([]);
  const defaultZones = [
    {
      id: "18c946df-f75c-42ed-a773-12f59c076c3a",
      coordinates: [
        { lat: 25.292279253635176, lng: 51.54597453979493 },
        { lat: 25.255024108579505, lng: 51.54597453979493 },
        { lat: 25.282656103583108, lng: 51.60399608520509 },
        { lat: 25.28731256185848, lng: 51.57035045532228 },
      ],
    },
    {
      id: "3a30552f-4668-4764-bd17-25d26a91aff2",
      coordinates: [
        { lat: 25.282035228973562, lng: 51.50237255004884 },
        { lat: 25.24819275834607, lng: 51.49138622192384 },
        { lat: 25.247571707457617, lng: 51.528465079345715 },
        { lat: 25.271479874463235, lng: 51.53155498413087 },
      ],
    },
  ];

  // const refactoredZones = defaultZones.map((zone) => ({
  //   ...zone,
  //   coordinates: zone.coordinates.map((coord) => new google.maps.LatLng(coord.lat, coord.lng)),
  // }));

  useEffect(() => {
    const refactoredZones = zonesReference.map((zone) => {
      const currentCoordinates = zone.geometry?.coordinates?.map(
        (coord) => new google.maps.LatLng(Number(coord.lat), Number(coord.lon))
      );
      const defaultCoordinates = defaultZonePosition.map((coord) => new google.maps.LatLng(coord.lat, coord.lng));
      const coordinatesRefactor = zone.geometry?.coordinates ? currentCoordinates : defaultCoordinates;
      return {
        id: String(zone.id),
        name: zone.name,
        coordinates: coordinatesRefactor,
        defaultGeometry: coordinatesRefactor,
        updated: false,
      };
    });
    setZones(refactoredZones);
  }, [zonesReference]);

  const [zones, setZones] = useState<IZone[]>([]);
  const [overlays, setOverlays] = useState<IOverlay[]>([]);

  // console.log("overlays", overlays);
  // console.log("zones", zones);
  // console.log(
  //   "zones[3].defaultGeometry",
  //   zones[3]?.defaultGeometry?.map((coord) => ({ lat: coord.lat(), lng: coord.lng() }))
  // );
  // console.log(
  //   "zones[3].coordinates",
  //   zones[3]?.coordinates?.map((coord) => ({ lat: coord.lat(), lng: coord.lng() }))
  // );

  useEffect(() => {
    const anyZoneUpdated = zones.some((zone) => zone.updated);
    const overlayCreated = overlays.length > 0;

    if (anyZoneUpdated || overlayCreated) {
      setUpdateFlag(true);
    } else {
      setUpdateFlag(false);
    }
  }, [zones, overlays]);

  const saveChanges = async () => {
    const updatedZones = zones.filter((zone) => zone.updated);
    const updatedZonesRefactor = updatedZones.map((zone) => {
      const currentCoordinates = zone.coordinates?.map((coord) => ({ lat: coord.lat(), lon: coord.lng() }));
      return {
        id: zone.id,
        name: zone.name,
        geometry: {
          coordinates: currentCoordinates,
        },
      };
    });

    const checkAnyMissingName = overlays.some((overlay) => checkEmpty(overlay.get("name")));

    if (checkAnyMissingName) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: "Area name is missing",
        })
      );
      return;
    }

    // append overlays to updatedZonesRefactor
    updatedZonesRefactor.push(
      ...overlays.map((overlay) => ({
        id: undefined,
        name: overlay.get("name"),
        geometry: {
          coordinates: overlay
            .getPath()
            .getArray()
            .map((coord) => ({ lat: coord.lat(), lon: coord.lng() })),
        },
      }))
    );

    try {
      setLoading(true);
      await updateArea(updatedZonesRefactor);

      // Update defaultGeometry for all successfully saved zones
      setZones((currentZones) =>
        currentZones.map((zone) => {
          // If this zone was updated and saved successfully
          if (zone.updated) {
            return {
              ...zone,
              defaultGeometry: [...(zone.coordinates as google.maps.LatLng[])],
              updated: false,
            };
          }
          return zone;
        })
      );

      setOverlaysToRemove(overlays);
      setOverlays([]);

      // Show success message
      const updatedZonesCount = updatedZones.length;
      const newOverlaysCount = overlays.length;

      let successMessage = "";
      if (updatedZonesCount > 0 && newOverlaysCount > 0) {
        successMessage = `${updatedZonesCount} area(s) updated and ${newOverlaysCount} new area(s) created successfully`;
      } else if (updatedZonesCount > 0) {
        successMessage = `${updatedZonesCount} area(s) updated successfully`;
      } else if (newOverlaysCount > 0) {
        successMessage = `${newOverlaysCount} new area(s) created successfully`;
      }

      if (successMessage) {
        dispatch(
          setNotification({
            show: true,
            severity: "success",
            message: successMessage,
          })
        );
      }
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
      console.error("Error updating zones:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkPolygonOverlap = useCallback(
    (targetPolygon: google.maps.LatLng[], excludeId?: string): boolean => {
      const OVERLAP_THRESHOLD = 0.0001; // Threshold for edge alignment tolerance

      // Helper function to check if a point is inside a polygon using ray casting
      const isPointInPolygon = (point: google.maps.LatLng, polygon: google.maps.LatLng[]): boolean => {
        let inside = false;
        const x = point.lat();
        const y = point.lng();

        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
          const xi = polygon[i].lat();
          const yi = polygon[i].lng();
          const xj = polygon[j].lat();
          const yj = polygon[j].lng();

          if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
            inside = !inside;
          }
        }
        return inside;
      };

      // Helper function to calculate polygon area using shoelace formula
      const calculatePolygonArea = (polygon: google.maps.LatLng[]): number => {
        let area = 0;
        for (let i = 0; i < polygon.length; i++) {
          const j = (i + 1) % polygon.length;
          area += polygon[i].lat() * polygon[j].lng();
          area -= polygon[j].lat() * polygon[i].lng();
        }
        return Math.abs(area) / 2;
      };

      // Helper function to check if two polygons have significant overlap
      const hasSignificantOverlap = (poly1: google.maps.LatLng[], poly2: google.maps.LatLng[]): boolean => {
        // Check if any vertices of poly1 are inside poly2
        const poly1PointsInPoly2 = poly1.filter((point) => isPointInPolygon(point, poly2));

        // Check if any vertices of poly2 are inside poly1
        const poly2PointsInPoly1 = poly2.filter((point) => isPointInPolygon(point, poly1));

        // If no points are inside either polygon, they might still overlap at edges
        if (poly1PointsInPoly2.length === 0 && poly2PointsInPoly1.length === 0) {
          return false;
        }

        // Calculate approximate overlap area based on overlapping points
        const totalOverlapPoints = poly1PointsInPoly2.length + poly2PointsInPoly1.length;
        const poly1Area = calculatePolygonArea(poly1);
        const poly2Area = calculatePolygonArea(poly2);
        const minArea = Math.min(poly1Area, poly2Area);

        // If overlap is significant relative to the smaller polygon area, consider it an overlap
        const overlapRatio = totalOverlapPoints / Math.max(poly1.length, poly2.length);

        return overlapRatio > OVERLAP_THRESHOLD && minArea > 0;
      };

      // Check against existing zones
      for (const zone of zones) {
        if (excludeId && zone.id === excludeId) continue;
        if (!zone.coordinates || zone.coordinates.length < 3) continue;

        if (hasSignificantOverlap(targetPolygon, zone.coordinates)) {
          return true;
        }
      }

      // Check against existing overlays
      for (const overlay of overlays) {
        if (excludeId && overlay.id === excludeId) continue;
        if (!overlay.coordinates || overlay.coordinates.length < 3) continue;

        if (hasSignificantOverlap(targetPolygon, overlay.coordinates)) {
          return true;
        }
      }

      return false;
    },
    [zones, overlays]
  );

  const saveZone = useCallback(
    async (zone: IZone) => {
      try {
        setLoading(true);
        const { id, name } = zone;
        await updateArea([
          {
            id,
            name,
            geometry: {
              coordinates: zone.coordinates?.map((coord) => ({ lat: coord.lat(), lon: coord.lng() })),
            },
          },
        ]);

        // Update the zone state to reflect the successful save
        setZones((currentZones) =>
          currentZones.map((z) =>
            z.id === zone.id
              ? { ...z, defaultGeometry: [...(zone.coordinates as google.maps.LatLng[])], updated: false }
              : z
          )
        );
      } catch (error: any) {
        dispatch(
          setNotification({
            show: true,
            severity: "error",
            message: error.response?.data?.message || error.message,
          })
        );
        console.error("Error updating zone:", error);
      } finally {
        setLoading(false);
      }
    },
    [zones]
  );

  // const createZone = useCallback(
  //   async (zone: IZone) => {
  //     try {
  //       setLoading(true);
  //       await createArea({
  //         id: zone.id,
  //         name: zone.name,
  //         geometry: {
  //           coordinates: zone.coordinates?.map((coord) => ({ lat: coord.lat(), lon: coord.lng() })),
  //         },
  //       });
  //     } catch (error: any) {
  //       dispatch(
  //         setNotification({
  //           show: true,
  //           severity: "error",
  //           message: error.response?.data?.message || error.message,
  //         })
  //       );
  //       console.error("Error creating zone:", error);
  //     } finally {
  //       setLoading(false);
  //     }
  //   },
  //   [zones]
  // );

  const deleteZone = async (zone: IZone) => {
    const answer = await confirmDelete();
    if (!answer) return;

    try {
      await deleteArea(zone);
      dispatch(
        setNotification({
          show: true,
          severity: "success",
          message: "Area has been deleted successfully",
        })
      );
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    }
  };

  async function initMap() {
    google.maps.importLibrary("maps") as Promise<google.maps.MapsLibrary>;
    google.maps.importLibrary("marker");
    google.maps.importLibrary("places");
    google.maps.importLibrary("drawing");
  }

  // Function to toggle label visibility
  const toggleLabelsVisibility = useCallback(() => {
    setLabelsVisible(!labelsVisible);

    // Update existing zone labels
    document.querySelectorAll(".gmaps-label-tag").forEach((label) => {
      const container = label.closest(".gmaps-container");
      // Check if this is a newly created overlay (not yet saved)
      const isNewOverlay = container && overlays.some((overlay) => overlay.get(POLYGON_ID_KEY) === container.id);

      // Don't hide labels for new overlays that haven't been saved yet
      if (!isNewOverlay) {
        if (labelsVisible) {
          label.classList.add("hidden");
        } else {
          label.classList.remove("hidden");
        }
      }
    });
  }, [labelsVisible, overlays]);

  return (
    <APIProvider apiKey={String(googleMapsApiKey)}>
      <GoogleMap
        zones={zones as IZone[]}
        overlaysToRemove={overlaysToRemove}
        setZones={setZones}
        saveZone={saveZone}
        setOverlays={setOverlays}
        deleteZone={deleteZone}
        createArea={(area) => updateArea([area])}
        updateArea={(area) => updateArea([area])}
        labelsVisible={labelsVisible}
        setLabelsVisible={setLabelsVisible}
      />
      {/* Always visible Hide/Show Labels button */}
      <Box
        sx={{
          position: "fixed",
          zIndex: 5,
          bottom: "48px",
          right: "32px",
          overflow: "auto",
          padding: "16px",
        }}
      >
        <Button
          sx={{
            boxShadow: "0px 4px 12px 0px rgba(0, 0, 0, 0.6)",
          }}
          variant="contained"
          color="secondary"
          onClick={toggleLabelsVisibility}
        >
          {labelsVisible ? "Hide Labels" : "Show Labels"}
        </Button>
      </Box>
      {/* Save button that slides in when there are changes */}
      <Slide direction="left" in={updateFlag}>
        <Box
          sx={{
            position: "fixed",
            zIndex: 5,
            bottom: "48px",
            right: updateFlag ? "200px" : "32px", // Adjust position when both buttons are visible
            overflow: "auto",
            padding: "16px",
          }}
        >
          <LoadingButton
            color="primary"
            loading={loading}
            variant="contained"
            onClick={() => saveChanges()}
            sx={{
              boxShadow: "0px 4px 12px 0px rgba(0, 0, 0, 0.6)",
            }}
          >
            Save
          </LoadingButton>
        </Box>
      </Slide>
      <ConfirmDialog />
      {/* <Button onClick={resetZones}>Reset</Button> */}
    </APIProvider>
  );
}
